import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Mail, ArrowLeft, CheckCircle, KeyRound } from "lucide-react";

export const Route = createFileRoute("/inloggegevens-vergeten")({
  head: () => ({
    meta: [
      { title: "Inloggegevens vergeten — Elvion Group Nederland" },
      { name: "description", content: "Wachtwoord vergeten? Vraag een reset-link aan en herstel direct toegang tot je klantaccount." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      return toast.error("Vul een geldig e-mailadres in");
    }
    setBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setBusy(false);
    if (error) return toast.error(error.message);
    setSent(true);
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="border-b border-border bg-hero-gradient">
          <div className="container mx-auto px-4 py-12 md:py-20">
            <Link
              to="/auth"
              className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Terug naar inloggen
            </Link>
            <div className="mt-6 max-w-2xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-medium text-white/80 backdrop-blur-sm">
                <KeyRound className="h-3 w-3" /> Beveiligde reset
              </span>
              <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight text-white">
                Inloggegevens vergeten
              </h1>
              <p className="mt-3 text-base md:text-lg text-white/70">
                Geen probleem. Vul je e-mailadres in en wij sturen je een veilige link om je wachtwoord opnieuw in te stellen.
              </p>
            </div>
          </div>
        </section>

        {/* Form */}
        <section className="container mx-auto px-4 py-12 md:py-16">
          <div className="mx-auto max-w-md">
            {!sent ? (
              <div className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm">
                <div className="mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Mail className="h-6 w-6" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">
                  Wachtwoord resetten
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  We sturen een e-mail met een persoonlijke reset-link die 1 uur geldig is.
                </p>

                <div className="mt-6 space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fp-email">E-mailadres</Label>
                    <Input
                      id="fp-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="jij@email.nl"
                      autoComplete="email"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") submit();
                      }}
                    />
                  </div>
                  <Button
                    onClick={submit}
                    disabled={busy}
                    className="w-full"
                  >
                    {busy ? "Bezig..." : "Stuur reset-link"}
                  </Button>
                </div>

                <div className="mt-6 text-center text-sm text-muted-foreground">
                  Weet je je wachtwoord weer?{" "}
                  <Link
                    to="/auth"
                    className="font-medium text-primary hover:underline"
                  >
                    Inloggen
                  </Link>
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-sm text-center">
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-grass/10 text-grass">
                  <CheckCircle className="h-8 w-8" />
                </div>
                <h2 className="text-xl font-semibold text-foreground">
                  E-mail verstuurd
                </h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Als er een account bestaat voor{" "}
                  <span className="font-medium text-foreground">{email}</span>,
                  ontvang je binnen enkele minuten een e-mail met instructies.
                </p>
                <p className="mt-4 text-xs text-muted-foreground">
                  Check ook je spamfolder. De link is 60 minuten geldig.
                </p>
                <div className="mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setSent(false);
                      setEmail("");
                    }}
                    className="w-full"
                  >
                    E-mail opnieuw versturen
                  </Button>
                </div>
                <div className="mt-4 text-center text-sm text-muted-foreground">
                  <Link
                    to="/auth"
                    className="font-medium text-primary hover:underline"
                  >
                    ← Terug naar inloggen
                  </Link>
                </div>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
